import React from 'react';
import { Alert, Box, Card, CardContent, Typography } from '@mui/material';
import { formatCurrency } from 'utils';
import { ClosedOutcomeSummary } from '../../types';

interface Props {
  summary: ClosedOutcomeSummary;
}

const formatDollar = (value: number) => (value ? formatCurrency(value, true) : '$0');

export const ClosedOutcomeSummaryStrip = ({ summary }: Props) => {
  const { totalClosed, closedWon, closedLost, percentWon, percentLost, valueWon, valueLost, wonMissingClosedOn } =
    summary;

  const items = [
    { label: 'Total closed', value: totalClosed.toLocaleString() },
    {
      label: 'Closed won',
      value: closedWon.toLocaleString(),
      sub: `${percentWon}%`,
      color: 'success.main',
    },
    {
      label: 'Closed lost',
      value: closedLost.toLocaleString(),
      sub: `${percentLost}%`,
      color: 'error.main',
    },
    { label: 'Value won', value: formatDollar(valueWon), color: 'success.main' },
    { label: 'Value lost', value: formatDollar(valueLost), color: 'error.main' },
  ];

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(5, 1fr)',
          },
          gap: 1.5,
        }}
      >
        {items.map(item => (
          <Card key={item.label} variant="outlined">
            <CardContent sx={{ p: '12px 16px !important' }}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                textTransform="uppercase"
                letterSpacing={0.4}
                display="block"
              >
                {item.label}
              </Typography>
              <Typography variant="h6" fontWeight={700} color={item.color} sx={{ overflowWrap: 'anywhere' }}>
                {item.value}
                {item.sub && (
                  <Typography component="span" variant="body2" color="text.secondary" ml={0.75}>
                    {item.sub}
                  </Typography>
                )}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
      {wonMissingClosedOn > 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>
          {wonMissingClosedOn.toLocaleString()} won deal(s) have no closed on date and are excluded from the won totals.
        </Alert>
      )}
    </Box>
  );
};
